import useSWR, { KeyedMutator } from "swr"

import { AppError } from "../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../errors/recognisedErrors"
import { objectOnCondition } from "../../../../utils/general/generalUtils"
import { postClientProtected } from "../../clients/clients"
import { installerSingleApplicationsMapper } from "../../mappers/installerInterface/installerApplicationsMapper"
import { IApplication } from "../../types/installerInterface/installerApplicationTypes"
import { INSTALLER_SINGLE_APPLICATION } from "../../urls/hybrid"

export type TGetInstalllerSingleApplication = {
    resMapped: IUseInstallerSingleApplication | undefined
}

export interface IUseInstallerSingleApplication {
    application: IApplication | undefined
    isLoading: boolean
    isValidating: boolean
    error: AppError | undefined
    mutate: KeyedMutator<IApplication>
}

/**
 * Custom hook for fetching a single installer application.
 * @param {string} applicationId - The ID of the application to fetch.
 * @returns {IUseInstallerSingleApplication} - The result of the hook, including the application data, loading state, error, and mutate function.
 */
const useInstallerSingleApplication = (
    applicationId: string,
    rapidRevalidate = false
): IUseInstallerSingleApplication => {
    const { data, error, isLoading, isValidating, mutate } = useSWR(
        INSTALLER_SINGLE_APPLICATION(applicationId),
        async (url: string): Promise<IApplication> => {
            try {
                const res = await postClientProtected(url, null)
                return installerSingleApplicationsMapper.mapSingle(res)
            } catch {
                throw invalidApiDataError(
                    "An error occurred while fetching the installer application."
                )
            }
        },
        {
            ...objectOnCondition(rapidRevalidate, { refreshInterval: 3000 }),
        }
    )

    return {
        application: data,
        isLoading,
        isValidating,
        error,
        mutate,
    }
}

export default useInstallerSingleApplication
